import { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { z } from "zod";
import { ContactLanguages } from "@/briefpoint-client";
import FormFieldError from "@/components/FormFieldError";
import Help from "@/components/Help";
import "./AddEditClientModal.scss";
import { useForm } from "@tanstack/react-form";
import { useClientApi, useCredentialApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { getClientsOptions } from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import { isResponseError } from "@/lib/type-guards";
import type { ClientResponse, UserResponse } from "@/lib/types";
import { getFieldError } from "@/lib/utils";
import Attorneys from "./Attorneys";

interface Props {
  title: string;
  submitButtonText: string;
  isShow: boolean;
  client?: ClientResponse;
  users: UserResponse[] | null;
  onClose: () => void;
  onClientSaved?: (client: ClientResponse) => void;
}

const formSchema = z.object({
  clientName: z.string().trim().min(1, "Client name is required"),
  contactEmail: z
    .string()
    .trim()
    .min(1, "Email address is required")
    .email("Invalid email address"),
  contactFirstName: z.string().trim(),
  contactLastName: z.string().trim(),
  respondentName: z.string().trim(),
  alias: z.string().trim(),
  contactLanguage: z.nativeEnum(ContactLanguages),
});

export default function AddEditClientModal(props: Props) {
  const { user } = useAuth();
  const clientApi = useClientApi();
  const credentialApi = useCredentialApi();

  const [attorneys, setAttorneys] = useState(props.client?.attorneys ?? []);
  const [showAlert, setShowAlert] = useState(true);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const form = useForm({
    defaultValues: {
      clientName: props.client?.name ?? "",
      contactEmail: props.client?.contactEmail ?? "",
      contactFirstName: props.client?.contactFirstName ?? "",
      contactLastName: props.client?.contactLastName ?? "",
      respondentName: props.client?.respondentName ?? "",
      alias: props.client?.aliases ?? "",
      contactLanguage:
        props.client?.contactLanguage ?? ContactLanguages.EnglishUs,
    },
    validators: {
      onChange: formSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const clientData = {
          ...props.client,
          name: value.clientName,
          aliases: value.alias,
          attorneys: attorneys,
          respondentName: value.respondentName,
          contactFirstName: value.contactFirstName,
          contactLastName: value.contactLastName,
          contactEmail: value.contactEmail,
          contactLanguage: value.contactLanguage,
          lastActivity: new Date(Date.now()), //not passed to the back end, used to update the local model to display recently updated
        };

        // if the client has an id, we are editing an existing client
        if (props.client?.id) {
          if (
            !!clientData.contactEmail.length &&
            value.contactEmail !== props.client?.contactEmail
          ) {
            await credentialApi.credentialSync({
              clientId: props.client.id,
              newEmail: clientData.contactEmail || "",
            });
          }

          await clientApi.clientPut({
            firmId: user?.firmId || "",
            id: props.client.id || "",
            client: clientData,
          });
        } else {
          // if the client has no id, we are creating a new client
          await clientApi.clientPost({
            firmId: user?.firmId || "",
            client: clientData,
          });
        }

        props.onClientSaved?.(clientData as ClientResponse);
        handleClose();
        queryClient.invalidateQueries({
          ...getClientsOptions(clientApi, user),
        });
      } catch (error: unknown) {
        if (isResponseError(error) && error.status === 409) {
          setEmailError(
            "This email is already in use by another client. Please use another one.",
          );
        } else {
          setEmailError(
            "Something has gone wrong please try again, or reload the page.",
          );
        }
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const handleClose = () => {
    form.reset();
    setAttorneys(props.client?.attorneys ?? []);
    setEmailError(undefined);
    props.onClose();
  };

  return (
    <Modal
      show={props.isShow}
      onHide={handleClose}
      size="lg"
      className="add-edit-client"
      backdrop="static"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="fw-normal">{props.title}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit} noValidate>
        <Modal.Body>
          {!props.client && showAlert && (
            <Alert
              variant="info"
              dismissible
              onClose={() => setShowAlert(false)}
            >
              <b>Did you know?</b> You don't need to create a Client to upload
              documents, you can add the Client during Document Confirmation.
            </Alert>
          )}
          <div className="client-input-row">
            <label htmlFor="client-name" className="required">
              Client Name
            </label>
            <form.Field
              name="clientName"
              children={(field) => (
                <>
                  <Form.Control
                    id="client-name"
                    required
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FormFieldError errorMessage={getFieldError(field)} />
                </>
              )}
            />
          </div>
          <div className="client-input-row">
            <div className="d-flex gap-1 align-items-center">
              <label htmlFor="client-refer">Refer to Client As</label>
              <Help
                id="refer-help"
                text="Will be used when selecting `Custom` for `Refer to responding party as` during Document Review."
              />
            </div>

            <form.Field
              name="respondentName"
              children={(field) => (
                <>
                  <Form.Control
                    id="client-refer"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FormFieldError errorMessage={getFieldError(field)} />
                </>
              )}
            />
          </div>
          <div className="client-input-row full">
            <div className="d-flex gap-1 align-items-center">
              <label htmlFor="client-aliases">Aliases</label>
              <Help
                id="aliases-help"
                text="Will be used when determining the Client for uploaded Documents if opposing council refers to them differently than you do. Separate aliases with a `;`."
              />
            </div>
            <form.Field
              name="alias"
              children={(field) => (
                <>
                  <Form.Control
                    id="client-aliases"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FormFieldError errorMessage={getFieldError(field)} />
                </>
              )}
            />
          </div>
          <h4>Client Contact Information</h4>
          <div className="client-input-row contact">
            <label htmlFor="contact-first">First Name</label>
            <form.Field
              name="contactFirstName"
              children={(field) => (
                <>
                  <Form.Control
                    id="contact-first"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FormFieldError errorMessage={getFieldError(field)} />
                </>
              )}
            />
          </div>
          <div className="client-input-row contact">
            <label htmlFor="contact-last">Last Name</label>
            <form.Field
              name="contactLastName"
              children={(field) => (
                <>
                  <Form.Control
                    id="contact-last"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FormFieldError errorMessage={getFieldError(field)} />
                </>
              )}
            />
          </div>
          <div className="client-input-row contact">
            <label htmlFor="contact-email" className="required">
              Email Address
            </label>
            <form.Field
              name="contactEmail"
              children={(field) => (
                <>
                  <Form.Control
                    id="contact-email"
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      setEmailError(undefined);
                    }}
                  />
                  <FormFieldError
                    errorMessage={
                      emailError || getFieldError(field) || undefined
                    }
                  />
                </>
              )}
            />
          </div>
          <div className="client-input-row contact">
            <label className="form-label" htmlFor="lng-selection">
              Primary Language
            </label>
            <form.Field
              name="contactLanguage"
              children={(field) => (
                <Form.Group id="lng-selection">
                  <Form.Check
                    id="lng-selection-yes"
                    type="radio"
                    label="English"
                    inline
                    checked={field.state.value !== ContactLanguages.Spanish}
                    onChange={() =>
                      field.handleChange(
                        field.state.value === ContactLanguages.Spanish
                          ? ContactLanguages.EnglishUs
                          : ContactLanguages.Spanish,
                      )
                    }
                  />
                  <Form.Check
                    id="lng-selection-no"
                    type="radio"
                    label="Spanish"
                    inline
                    checked={field.state.value === ContactLanguages.Spanish}
                    onChange={() =>
                      field.handleChange(
                        field.state.value === ContactLanguages.Spanish
                          ? ContactLanguages.EnglishUs
                          : ContactLanguages.Spanish,
                      )
                    }
                  />
                </Form.Group>
              )}
            />
          </div>
          <Attorneys
            users={props.users}
            assignedAttorneys={attorneys}
            topLevelAttorneys={[]}
            setAttorneys={setAttorneys}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={handleClose}>
            Cancel
          </Button>
          <form.Subscribe
            selector={(state) => ({
              isValid: state.isValid,
              isSubmitting: state.isSubmitting,
            })}
          >
            {(formState) => (
              <Button
                variant="primary"
                type="submit"
                disabled={formState.isSubmitting || !formState.isValid}
              >
                {formState.isSubmitting ? "Saving..." : props.submitButtonText}
              </Button>
            )}
          </form.Subscribe>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
